import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "./store";

/**
 * App-level UI state (FR-A8: user settings live in app Redux): persisted
 * legend selection per chart, keyed by a stable chart id.
 *
 * Charts dispatch on every ECharts `legendselectchanged` (via the library
 * event bus, filtered by `context.chartId`) and read the map back through
 * `EChart`'s `legendSelected` prop — so toggles survive unmount/remount,
 * e.g. navigating Dashboard → Oee → Dashboard.
 */
export interface ChartLegendState {
  /** chartId → (series name → selected). Charts without toggles are absent. */
  byChartId: Record<string, Record<string, boolean>>;
}

const initialState: ChartLegendState = {
  byChartId: {},
};

const chartLegendSlice = createSlice({
  name: "chartLegend",
  initialState,
  reducers: {
    /** Replaces the whole selection map for one chart (ECharts reports it whole). */
    setChartLegend(
      state,
      action: PayloadAction<{
        chartId: string;
        selected: Record<string, boolean>;
      }>,
    ) {
      state.byChartId[action.payload.chartId] = action.payload.selected;
    },
    /** Forget one chart's persisted selection (or all charts when no id). */
    clearChartLegend(state, action: PayloadAction<{ chartId?: string }>) {
      if (action.payload.chartId) {
        delete state.byChartId[action.payload.chartId];
      } else {
        state.byChartId = {};
      }
    },
  },
});

export const { setChartLegend, clearChartLegend } = chartLegendSlice.actions;

export function selectChartLegend(
  state: RootState,
  chartId: string,
): Record<string, boolean> | undefined {
  return state.chartLegend.byChartId[chartId];
}

export const chartLegendReducer = chartLegendSlice.reducer;
